import React, { useCallback, useRef, useState } from 'react';
import { SIGNATURE_VALIDATION_MAX_UPLOAD_BYTES } from '../../services/documentSignatureService';

const MAX_UPLOAD_MB = Math.round(SIGNATURE_VALIDATION_MAX_UPLOAD_BYTES / (1024 * 1024));

interface ValidatePageLandingProps {
  loading: boolean;
  error: string;
  onConsult: (query: string) => void;
  onUpload: (file: File, publicId?: string) => void;
}

export const ValidatePageLanding: React.FC<ValidatePageLandingProps> = ({
  loading,
  error,
  onConsult,
  onUpload,
}) => {
  const [query, setQuery] = useState('');
  const [uploadPublicId, setUploadPublicId] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    (file: File | undefined) => {
      if (!file) {
        return;
      }

      setUploadError('');

      if (!file.name.toLowerCase().endsWith('.pdf') && file.type !== 'application/pdf') {
        setUploadError('Envie apenas arquivos PDF.');
        return;
      }

      if (file.size > SIGNATURE_VALIDATION_MAX_UPLOAD_BYTES) {
        setUploadError(`O arquivo excede o limite de ${MAX_UPLOAD_MB} MB.`);
        return;
      }

      const optionalPublicId = uploadPublicId.trim() || undefined;
      onUpload(file, optionalPublicId);
    },
    [onUpload, uploadPublicId]
  );

  const onDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
    handleFile(event.dataTransfer.files[0]);
  };

  return (
    <div className="mt-6 space-y-8">
      <section>
        <h2 className="text-sm font-semibold text-slate-800">Consultar por ID ou hash</h2>
        <p className="mt-1 text-xs text-slate-500">
          Cole o <span className="font-medium text-slate-700">ID público</span> do QR code / selo do documento (hexadecimal,
          sem caracteres <code className="rounded bg-slate-100 px-1">:</code>) ou o SHA-256 completo do PDF. O endereço IP
          da evidência de assinatura não é um identificador válido para esta consulta.
        </p>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="ID público (hex) ou SHA-256"
            className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            disabled={loading}
          />
          <button
            type="button"
            onClick={() => onConsult(query)}
            disabled={loading || !query.trim()}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Consultar
          </button>
        </div>
      </section>

      <section>
        <h2 className="text-sm font-semibold text-slate-800">Enviar PDF</h2>
        <p className="mt-1 text-xs text-slate-500">
          Compare a integridade do arquivo com os registros Know InDoc (original ou documento final assinado).
        </p>
        <div className="mt-3">
          <label className="block text-xs font-medium text-slate-600" htmlFor="upload-public-id">
            ID público (opcional)
          </label>
          <input
            id="upload-public-id"
            type="text"
            value={uploadPublicId}
            onChange={(e) => setUploadPublicId(e.target.value)}
            placeholder="Restringe a comparação quando você tem QR + arquivo"
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            disabled={loading}
          />
        </div>
        <div
          role="button"
          tabIndex={0}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              fileInputRef.current?.click();
            }
          }}
          className={`mt-3 flex flex-col items-center justify-center rounded-lg border-2 border-dashed px-6 py-10 text-center transition-colors ${
            dragOver ? 'border-blue-400 bg-blue-50' : 'border-slate-300 bg-slate-50'
          }`}
        >
          <p className="text-sm text-slate-600">Arraste um PDF aqui ou</p>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={loading}
            className="mt-2 text-sm font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
          >
            selecione o arquivo
          </button>
          <p className="mt-2 text-xs text-slate-400">Somente .pdf — até {MAX_UPLOAD_MB} MB</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </div>
      </section>

      {(error || uploadError) && (
        <p className="text-sm text-red-600">{error || uploadError}</p>
      )}
      {loading && <p className="text-sm text-slate-500">Processando…</p>}
    </div>
  );
};
